// Stand-in for the Compelling Evidence dashboard endpoints until the backend exposes them.
// State, KPI and the case list are all derived from one fixed set of cases, so the program
// cards, the KPI cards and the table always agree. Delete this file together with the mock
// calls in services.js once the endpoints land.
import dayjs from "dayjs"

import { CASE_OUTCOME } from "../constants/outcomes"
import { CE_PAGE_STATE } from "../constants/page-states"

// Flip these to see the overlays.
const PAGE_STATE = CE_PAGE_STATE.ACTIVE
const HAS_INTEGRATION = true

const CASE_COUNT = 24
// Charged only on a stopped dispute, as a share of the transaction (the prototype's rate).
const FEE_RATE = 0.2
const OUTCOME_CYCLE = [
  CASE_OUTCOME.STOPPED,
  CASE_OUTCOME.NOT_STOPPED,
  CASE_OUTCOME.AWAITING,
  CASE_OUTCOME.STOPPED,
  CASE_OUTCOME.NOT_STOPPED,
  CASE_OUTCOME.STOPPED
]

export const CASES = Array.from({ length: CASE_COUNT }, (_, i) => {
  const amount = 40 + ((i * 37) % 260) + 0.99
  const outcome = OUTCOME_CYCLE[i % OUTCOME_CYCLE.length]
  return {
    id: i + 1,
    date: dayjs()
      .subtract(i * 2, "day")
      .subtract((i * 53) % 600, "minute")
      .toISOString(),
    card: String(1000 + ((i * 7919) % 9000)).slice(-4),
    amount,
    program: i % 8 < 5 ? "ce" : "fpt",
    outcome,
    fee_charged: outcome === CASE_OUTCOME.STOPPED ? Math.round(amount * FEE_RATE * 100) / 100 : 0
  }
})

const PROGRAM_STATUS = { ce: "active", fpt: "pending" }

const sum = (rows, key) => Math.round(rows.reduce((acc, r) => acc + r[key], 0) * 100) / 100

// An untouched Card filter arrives as `[]` (serializeData keeps empty arrays), which must read as
// "no filter", not as "card number ''".
const listParam = (value) => {
  const items = String(value ?? "")
    .split(",")
    .filter(Boolean)
  return items.length && !items.includes("all") ? items : null
}

const inRange = (value, min, max) =>
  (min == null || value >= Number(min)) && (max == null || value <= Number(max))

const byProgram = (program) => {
  const programs = listParam(program)
  return programs ? CASES.filter((c) => programs.includes(c.program)) : CASES
}

const compare = (a, b) => (a > b) - (a < b)

export const mockResponse = (data, delayMs = 300) =>
  new Promise((resolve) => setTimeout(() => resolve({ ok: true, status: 200, data }), delayMs))

// Counts only once there is a dashboard to show; behind an overlay the cards read zero.
export const mockState = () => {
  const countOf = (program) => (PAGE_STATE === CE_PAGE_STATE.ACTIVE ? byProgram(program).length : 0)

  return mockResponse({
    page_state: PAGE_STATE,
    has_integration: HAS_INTEGRATION,
    programs: [
      { program: "ALL", case_count: countOf("all"), status: "active" },
      ...Object.entries(PROGRAM_STATUS).map(([program, status]) => ({
        program: program.toUpperCase(),
        case_count: countOf(program),
        status
      }))
    ]
  })
}

export const mockKpi = ({ program }) => {
  const rows = byProgram(program)
  const stopped = rows.filter((c) => c.outcome === CASE_OUTCOME.STOPPED)
  const decided = rows.filter((c) => c.outcome !== CASE_OUTCOME.AWAITING)

  return mockResponse({
    disputes_stopped: stopped.length,
    stopped_rate: decided.length ? (stopped.length / decided.length) * 100 : 0,
    revenue_protected: sum(stopped, "amount"),
    fees_charged: sum(rows, "fee_charged")
  })
}

export const mockCases = ({ page = 1, page_size: pageSize = 20, ordering, ...params }) => {
  const outcomes = listParam(params.outcome)
  const cards = listParam(params.card_last4)
  const [from, to] = params.date ? params.date.split(",") : []

  const rows = byProgram(params.program).filter(
    (c) =>
      (!outcomes || outcomes.includes(c.outcome)) &&
      (!cards || cards.includes(c.card)) &&
      (!from || c.date >= from) &&
      (!to || c.date <= to) &&
      inRange(c.amount, params.amount_min, params.amount_max) &&
      inRange(c.fee_charged, params.fee_min, params.fee_max)
  )

  const field = ordering?.replace(/^-/, "") || "date"
  const direction = ordering && !ordering.startsWith("-") ? 1 : -1
  const sorted = [...rows].sort((a, b) => compare(a[field], b[field]) * direction)

  return mockResponse({
    count: sorted.length,
    results: sorted.slice((page - 1) * pageSize, page * pageSize)
  })
}

const CE_FAQ = [
  {
    title: "What is Compelling Evidence?",
    text: "Compelling Evidence stops fraud disputes before they become chargebacks. When a cardholder tells their bank they didn't make a purchase, the card network compares it with earlier orders the same cardholder placed with you. If the details match, the dispute is stopped."
  },
  {
    title: "What programs am I enrolled in?",
    text: "Two programs, enrolled together: Visa Compelling Evidence (via Verifi) and Mastercard First-Party Trust (via Ethoca). They work on top of Order Insight and Consumer Clarity."
  },
  {
    title: "How does it work?",
    text: "When a fraud dispute arrives, we send the network the details of the disputed order: IP address, device, account, delivery and contact details. The network compares them with your past orders on the same card. If they match, Visa blocks the dispute and Mastercard moves liability to the bank."
  },
  {
    title: "Which past orders are compared?",
    text: "Visa uses orders made 120–365 days before the dispute and needs at least 2 matching orders. Mastercard chooses which past orders to check."
  },
  {
    title: "Where can I see my charges?",
    text: "Every case and its fee appear in the table on the Compelling Evidence page, which you can filter and export. The total is shown in the Fees charged card and on your invoices."
  },
  {
    title: "How long does enrollment take?",
    text: "Enrollment usually takes up to 7 days. We'll email you when it's active. The first fraud disputes usually appear within 7 days after that."
  },
  {
    title: "What do I need to enroll?",
    text: "Order Insight and Consumer Clarity, a connected Shopify or Stripe store with the setup instructions completed, and your BIN and CAID."
  }
]

export const mockExport = () =>
  mockResponse({ message: "Export in progress, you will be notified once it's done." })

export const mockFaq = () => mockResponse(CE_FAQ)
